import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition, ParameterValue} from "@cm/graph-v3"
import {parameterValue} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type MathOperator = "add" | "subtract" | "multiply" | "divide" | "min" | "max"
export type MathRounding = "none" | "floor" | "ceil" | "round"

export type MathNodeParameters = {
    description: string //human-readable, e.g. "legs / 4, rounded up"
    a: ParameterValue<number, PricingContext>
    b: ParameterValue<number, PricingContext>
    operator: MathOperator
    rounding?: MathRounding //applied to the result, defaults to "none"
}

const mathNodeDefinition = {
    nodeClass: "MathNode",
    validation: {
        parameters: z.object({
            description: z.string(),
            a: parameterValue(z.number()),
            b: parameterValue(z.number()),
            operator: z.union([z.literal("add"), z.literal("subtract"), z.literal("multiply"), z.literal("divide"), z.literal("min"), z.literal("max")]),
            rounding: z.optional(z.union([z.literal("none"), z.literal("floor"), z.literal("ceil"), z.literal("round")])),
        }),
        returns: z.number(),
    },
} satisfies GraphNodeDefinition<number, MathNodeParameters>

/** Combines two numbers (constants or number-valued nodes) with a binary operator. Chain several MathNodes for more complex expressions. */
@registerPricingGraphNode
export class MathNode extends PricingNode<number, MathNodeParameters> {
    public override get definition() {
        return mathNodeDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        const [a, b] = await Promise.all([evaluator.get(this.parameters.a), evaluator.get(this.parameters.b)])
        return applyRounding(applyOperator(this.parameters.operator, a, b), this.parameters.rounding ?? "none")
    }
}

function applyOperator(operator: MathOperator, a: number, b: number): number {
    switch (operator) {
        case "add":
            return a + b
        case "subtract":
            return a - b
        case "multiply":
            return a * b
        case "divide":
            //returning 0 would silently remove priced items from the list, so fail loudly instead
            if (b === 0) throw new Error("MathNode: division by zero")
            return a / b
        case "min":
            return Math.min(a, b)
        case "max":
            return Math.max(a, b)
    }
}

function applyRounding(value: number, rounding: MathRounding): number {
    switch (rounding) {
        case "none":
            return value
        case "floor":
            return Math.floor(value)
        case "ceil":
            return Math.ceil(value)
        case "round":
            return Math.round(value)
    }
}
